import { useCallback, useEffect, useState } from "react";
import { IAlert } from "../interfaces/alert";
import {
  createMerchantsExport,
  deleteMerchantsExport,
  getMerchantsExports,
  IMerchantsExport,
} from "../requests/merchantsExportRequests";
import { ERROR, SUCCESS } from "../utils/constants";

const ACTIVE_STATUSES: IMerchantsExport["status"][] = ["pending", "processing"];

interface IUseMerchantsExport {
  exports: IMerchantsExport[];
  loading: boolean;
  creating: boolean;
  blocked: boolean;
  showConfirmModal: boolean;
  alert: IAlert;
  handleCreate: () => void;
  handleConfirmCreate: () => Promise<void>;
  handleCancelConfirm: () => void;
  handleDelete: (id: number) => Promise<void>;
  clearAlert: () => void;
}

export const useMerchantsExport = (): IUseMerchantsExport => {
  const [exports, setExports] = useState<IMerchantsExport[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [alert, setAlert] = useState<IAlert>({ showAlert: false });

  const clearAlert = useCallback(() => setAlert({ showAlert: false }), []);

  const fetchExports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMerchantsExports();
      setExports(data);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    } finally {
      setLoading(false);
    }
  }, []);

  const doCreate = async (currentExports: IMerchantsExport[]) => {
    setCreating(true);
    clearAlert();
    try {
      const activeExports = currentExports.filter(({ status }) =>
        ACTIVE_STATUSES.includes(status),
      );
      await Promise.all(activeExports.map(({ id }) => deleteMerchantsExport(id)));
      await createMerchantsExport();
      setBlocked(true);
      setAlert({
        typeOfAlert: SUCCESS,
        message:
          "Exportación solicitada. El archivo estará disponible en los próximos minutos.",
        showAlert: true,
      });
      await fetchExports();
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    } finally {
      setCreating(false);
    }
  };

  const hasPendingExport = exports.some(({ status }) =>
    ACTIVE_STATUSES.includes(status),
  );

  const handleCreate = () => {
    if (hasPendingExport) {
      setShowConfirmModal(true);
    } else {
      doCreate(exports);
    }
  };

  const handleConfirmCreate = async () => {
    setShowConfirmModal(false);
    await doCreate(exports);
  };

  const handleCancelConfirm = () => setShowConfirmModal(false);

  const handleDelete = async (id: number) => {
    try {
      await deleteMerchantsExport(id);
      setExports((prev) => prev.filter((e) => e.id !== id));
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };

  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  return {
    exports,
    loading,
    creating,
    blocked,
    showConfirmModal,
    alert,
    handleCreate,
    handleConfirmCreate,
    handleCancelConfirm,
    handleDelete,
    clearAlert,
  };
};
